// Twilio configuration
const TWILIO_ACCOUNT_SID = import.meta.env.VITE_TWILIO_ACCOUNT_SID;
const TWILIO_AUTH_TOKEN = import.meta.env.VITE_TWILIO_AUTH_TOKEN;
const TWILIO_PHONE_NUMBER = import.meta.env.VITE_TWILIO_PHONE_NUMBER;

// Note: Twilio SDK doesn't work in browser environment
// For production, SMS should be sent through a backend API/Edge Function
let twilioClient = null;

// SMS Templates
const templates = {
  appointmentConfirmation: (data) => 
    `ShedX: Your service appointment is confirmed for ${data.date} at ${data.time}. Reply CANCEL to cancel or call (940) 969-2509.`,
  
  appointmentReminder: (data) => 
    `Reminder: Your service appointment at ShedX is tomorrow ${data.date} at ${data.time}. Reply CONFIRM to confirm or CANCEL to cancel.`,
  
  paymentReminder: (data) => 
    `ShedX: Payment of $${data.amount} is due on ${data.dueDate}. Pay online or call (940) 969-2509 for assistance.`,
  
  leadResponse: (data) => 
    `Thank you for your interest in the ${data.vehicle}! A sales representative from ShedX will contact you shortly. Call (940) 969-2509 for immediate assistance.`,
  
  serviceComplete: (data) => 
    `ShedX: Your ${data.vehicle} is ready for pickup! Total: $${data.amount}. We're open until 5PM. Call (940) 969-2509 with questions.`,
  
  promotionalOffer: (data) => 
    `ShedX Special: ${data.offer}. Valid until ${data.expiry}. Show this text to redeem. Reply STOP to opt out.`,
  
  testDriveConfirmation: (data) => 
    `Test drive confirmed for ${data.date} at ${data.time} - ${data.vehicle}. Please bring your driver's license. Reply CANCEL to cancel.`,
  
  documentReminder: (data) => 
    `ShedX: Please bring the following documents for your appointment: ${data.documents}. Reply YES when ready.`,
  
  dealUpdate: (data) => 
    `Good news! Your financing for the ${data.vehicle} has been approved. Please come in to finalize paperwork. Call (940) 969-2509.`,
  
  welcomeMessage: (data) => 
    `Welcome to ShedX! Thank you for your purchase of the ${data.vehicle}. Save this number for service reminders and exclusive offers.`
};

class SMSService {
  constructor() {
    this.client = twilioClient;
    this.fromNumber = TWILIO_PHONE_NUMBER;
    this.messageHistory = [];
  }

  // Validate phone number format
  validatePhoneNumber(phone) {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');
    
    // Check if it's a valid US number (10 digits or 11 with country code)
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+${cleaned}`;
    }
    
    throw new Error('Invalid phone number format. Please use a 10-digit US number.');
  }

  // Send SMS message via Supabase Edge Function
  async sendSMS(to, message, options = {}) {
    try {
      const formattedPhone = this.validatePhoneNumber(to);
      
      // Check if SMS is configured
      if (!TWILIO_ACCOUNT_SID || !TWILIO_AUTH_TOKEN || !TWILIO_PHONE_NUMBER) {
        console.warn('SMS not configured - simulating send');
        return {
          success: true,
          messageId: `sim_${Date.now()}`,
          status: 'simulated',
          note: 'Configure Twilio credentials to enable SMS'
        };
      }

      // Call Supabase Edge Function
      const { supabase } = await import('../lib/supabase');
      const { data, error } = await supabase.functions.invoke('send-sms', {
        body: { 
          to: formattedPhone, 
          message,
          template: options.template,
          data: options.data
        }
      });

      if (error) throw error;

      // Store in local history
      this.messageHistory.push({
        id: data.messageId,
        to: formattedPhone,
        message,
        timestamp: new Date().toISOString(),
        status: data.status
      });

      return data;
    } catch (error) {
      console.error('SMS send error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Send appointment confirmation
  async sendAppointmentConfirmation(phone, appointmentData) {
    const message = templates.appointmentConfirmation({
      date: new Date(appointmentData.date).toLocaleDateString(),
      time: appointmentData.time
    });
    return this.sendSMS(phone, message);
  }

  // Send appointment reminder (typically day before)
  async sendAppointmentReminder(phone, appointmentData) {
    const message = templates.appointmentReminder({
      date: new Date(appointmentData.date).toLocaleDateString(),
      time: appointmentData.time
    });
    return this.sendSMS(phone, message);
  }

  // Send payment reminder
  async sendPaymentReminder(phone, paymentData) {
    const message = templates.paymentReminder({
      amount: paymentData.amount,
      dueDate: new Date(paymentData.dueDate).toLocaleDateString()
    });
    return this.sendSMS(phone, message);
  }

  // Send lead auto-response
  async sendLeadResponse(phone, leadData) {
    const message = templates.leadResponse({
      vehicle: `${leadData.year} ${leadData.make} ${leadData.model}`
    });
    return this.sendSMS(phone, message);
  }

  // Send service completion notification
  async sendServiceComplete(phone, serviceData) {
    const message = templates.serviceComplete({
      vehicle: `${serviceData.year} ${serviceData.make} ${serviceData.model}`,
      amount: serviceData.totalAmount
    });
    return this.sendSMS(phone, message);
  }

  // Send test drive confirmation
  async sendTestDriveConfirmation(phone, testDriveData) {
    const message = templates.testDriveConfirmation({
      date: new Date(testDriveData.date).toLocaleDateString(),
      time: testDriveData.time,
      vehicle: `${testDriveData.year} ${testDriveData.make} ${testDriveData.model}`
    });
    return this.sendSMS(phone, message);
  }

  // Send promotional message (with opt-out)
  async sendPromotionalMessage(phone, promoData) {
    const message = templates.promotionalOffer({
      offer: promoData.offer,
      expiry: new Date(promoData.expiry).toLocaleDateString()
    });
    return this.sendSMS(phone, message);
  }

  // Send document reminder
  async sendDocumentReminder(phone, documents) {
    const message = templates.documentReminder({
      documents: documents.join(', ')
    });
    return this.sendSMS(phone, message);
  }

  // Send deal status update
  async sendDealUpdate(phone, dealData) {
    const message = templates.dealUpdate({
      vehicle: `${dealData.year} ${dealData.make} ${dealData.model}`
    });
    return this.sendSMS(phone, message);
  }

  // Send welcome message after purchase
  async sendWelcomeMessage(phone, purchaseData) {
    const message = templates.welcomeMessage({
      vehicle: `${purchaseData.year} ${purchaseData.make} ${purchaseData.model}`
    });
    return this.sendSMS(phone, message);
  }

  // Send bulk messages (for campaigns)
  async sendBulkSMS(phoneNumbers, message, options = {}) {
    const results = [];
    
    // Rate limiting - Twilio recommends max 1 message per second
    for (const phone of phoneNumbers) {
      try {
        const result = await this.sendSMS(phone, message, options);
        results.push({ phone, ...result });
        
        // Wait 1 second between messages to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        results.push({ phone, success: false, error: error.message });
      }
    }
    
    return results;
  }

  // Get message status
  async getMessageStatus(messageId) {
    try {
      if (!this.client) {
        return { success: false, error: 'SMS service not configured' };
      }

      const message = await this.client.messages(messageId).fetch();
      return {
        success: true,
        status: message.status,
        errorCode: message.errorCode,
        errorMessage: message.errorMessage
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Handle incoming SMS (for webhooks)
  handleIncomingSMS(body, from) {
    const upperBody = body.toUpperCase().trim();
    
    // Handle opt-out
    if (['STOP', 'UNSUBSCRIBE', 'CANCEL'].includes(upperBody)) {
      // Add to opt-out list (would need database integration)
      return {
        action: 'opt-out',
        response: 'You have been unsubscribed from ShedX SMS updates.'
      };
    }
    
    // Handle appointment confirmations
    if (upperBody === 'CONFIRM') {
      return {
        action: 'confirm-appointment',
        response: 'Thank you! Your appointment is confirmed. See you soon!'
      };
    }
    
    // Handle appointment cancellations
    if (upperBody === 'CANCEL') {
      return {
        action: 'cancel-appointment',
        response: 'Your appointment has been cancelled. Call us to reschedule: (940) 969-2509'
      };
    }
    
    // Default response
    return {
      action: 'unknown',
      response: 'Thank you for your message. A representative will contact you soon, or call (940) 969-2509.'
    };
  }

  // Get message history
  getMessageHistory() {
    return this.messageHistory;
  }

  // Clear message history
  clearMessageHistory() {
    this.messageHistory = [];
  }
}

// Export singleton instance
const smsService = new SMSService();
export default smsService;
